import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileUp, Mail, Download } from 'lucide-react';

import { useToast } from '@/components/ui/use-toast';

const schema = z.object({
  tenderNo: z.string().min(1, 'Tender number is required'),
  tenderTitle: z.string().min(1, 'Tender title is required'),
  category: z.string().min(1, 'Category is required'),
  lot: z.string().optional(),
  legalName: z.string().min(1, 'Legal entity name is required'),
  tradingName: z.string().optional(),
  entityType: z.string().min(1, 'Entity type is required'),
  regNo: z.string().min(1, 'Registration number is required'),
  kraPin: z.string().min(1, 'KRA PIN is required'),
  vatStatus: z.string().optional(),
  contactName: z.string().min(1, 'Contact name is required'),
  contactEmail: z.string().email('Valid email is required'),
  contactPhone: z.string().min(1, 'Contact phone is required'),
  address: z.string().optional(),
  agpoCategory: z.string().optional(),
  agpoCertNo: z.string().optional(),
  boDisclosure: z.string().min(1, 'Beneficial ownership disclosure is required'),
  signName: z.string().min(1, 'Signatory name is required'),
  signTitle: z.string().min(1, 'Signatory title is required'),
  signDate: z.string().min(1, 'Date is required'),
  bidSecurity: z.string().optional(),
  ethics: z.boolean().refine(val => val === true, 'Ethics declaration is required'),
  conflict: z.boolean().refine(val => val === true, 'Conflict of interest declaration is required'),
  dpConsent: z.boolean().refine(val => val === true, 'Data protection consent is required'),
});

type FormData = z.infer<typeof schema>;

interface TenderRegistrationFormProps {
  onClose: () => void;
}

const TenderRegistrationForm: React.FC<TenderRegistrationFormProps> = ({ onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  
  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      signDate: new Date().toISOString().split('T')[0],
    }
  });

  const generatePDF = async (data: FormData) => {
    const pdf = new jsPDF();
    
    // Header with SACCO branding
    pdf.setFontSize(16);
    pdf.setTextColor(10, 111, 78); // Primary green color
    pdf.text('NATCON STAFF SACCO', 20, 20);
    
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    pdf.text('KCB Towers, Kenya Road', 140, 15);
    pdf.text('P.O. Box 21046-00100', 140, 20);
    pdf.text('Tel: +254111485951', 140, 25);
    pdf.text('Email: natconsacco@nca.go.ke', 140, 30);
    
    // Title
    pdf.setFontSize(14);
    pdf.setTextColor(10, 111, 78);
    pdf.text('TENDER REGISTRATION FORM', 20, 45);
    
    let yPosition = 60;
    
    // Form content
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    
    const fields = [
      { label: 'Tender Number:', value: data.tenderNo },
      { label: 'Tender Title:', value: data.tenderTitle },
      { label: 'Category:', value: data.category },
      { label: 'Lot:', value: data.lot || 'N/A' },
      { label: 'Legal Entity Name:', value: data.legalName },
      { label: 'Trading Name:', value: data.tradingName || 'N/A' },
      { label: 'Entity Type:', value: data.entityType },
      { label: 'Registration No.:', value: data.regNo },
      { label: 'KRA PIN:', value: data.kraPin },
      { label: 'VAT Status:', value: data.vatStatus || 'N/A' },
      { label: 'Contact Person:', value: data.contactName },
      { label: 'Contact Email:', value: data.contactEmail },
      { label: 'Contact Phone:', value: data.contactPhone },
      { label: 'Address:', value: data.address || 'N/A' },
      { label: 'AGPO Category:', value: data.agpoCategory || 'N/A' },
      { label: 'AGPO Cert No.:', value: data.agpoCertNo || 'N/A' },
      { label: 'Authorized Signatory:', value: data.signName },
      { label: 'Designation:', value: data.signTitle },
      { label: 'Date:', value: data.signDate },
    ];
    
    fields.forEach((field) => {
      if (yPosition > 270) {
        pdf.addPage();
        yPosition = 20;
      }
      pdf.setFont(undefined, 'bold');
      pdf.text(field.label, 20, yPosition);
      pdf.setFont(undefined, 'normal');
      pdf.text(field.value, 80, yPosition);
      yPosition += 8;
    });
    
    // Beneficial ownership (multiline)
    if (yPosition > 250) {
      pdf.addPage();
      yPosition = 20;
    }
    pdf.setFont(undefined, 'bold');
    pdf.text('Beneficial Ownership:', 20, yPosition);
    yPosition += 8;
    pdf.setFont(undefined, 'normal');
    const boLines = pdf.splitTextToSize(data.boDisclosure, 170);
    pdf.text(boLines, 20, yPosition);
    
    return pdf;
  };

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    
    try {
      // Generate PDF
      const pdf = await generatePDF(data);
      const pdfBlob = pdf.output('blob');
      const filename = `tender_application_${data.legalName.replace(/\s+/g, '_')}.pdf`;

      // Prepare email content
      const subjectText = 'Tender Application Submission';
      const bodyText = `Dear Natcon Staff Sacco Procurement Team,

I am writing to submit our tender application for the following:

Tender Number: ${data.tenderNo}
Tender Title: ${data.tenderTitle}
Company Name: ${data.legalName}
Contact Person: ${data.contactName}
Email: ${data.contactEmail}
Phone: ${data.contactPhone}

Please find the completed tender registration form attached. All required documents will be submitted as per the tender requirements.

We confirm that we have read and understood all the terms and conditions of the tender and comply with all the requirements stated.

Thank you for your consideration.

Best regards,
${data.signName}
${data.signTitle}
${data.legalName}`;

      // Try to share PDF with email app (mobile-friendly with proper email fields)
      if ('share' in navigator && 'canShare' in navigator) {
        const file = new File([pdfBlob], filename, { type: 'application/pdf' });
        
        if (navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              files: [file],
              title: subjectText,
              text: `To: natconsacco@nca.go.ke\nSubject: ${subjectText}\n\n${bodyText}`,
            });
            
            toast({ 
              title: 'Application submitted', 
              description: 'Select your email app - the PDF will be attached automatically.',
              duration: 5000
            });
            onClose();
            return;
          } catch (error: any) {
            // User cancelled or share failed, continue to fallback
            if (error.name !== 'AbortError') {
              console.warn('Share failed, using fallback:', error);
            }
          }
        }
      }

      // Fallback: Download PDF and open email with pre-filled content
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Create email with pre-filled content
      const subject = encodeURIComponent(subjectText);
      const body = encodeURIComponent(bodyText);
      const mailtoLink = `mailto:natconsacco@nca.go.ke?subject=${subject}&body=${body}`;
      
      // Wait a moment for download to start, then open email
      setTimeout(() => {
        window.location.href = mailtoLink;
        
        toast({ 
          title: 'PDF downloaded', 
          description: `Email opening - please attach the downloaded PDF file "${filename}" and send`,
          duration: 8000
        });
      }, 1000);

      onClose();
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({ 
        variant: 'destructive', 
        title: 'Submission failed', 
        description: 'Please try again or contact support.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader className="bg-gradient-primary text-primary-foreground">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-xl">Tender Registration Form</CardTitle>
            <p className="text-primary-foreground/90 text-sm mt-1">
              Apply for a specific tender/lot. Fields marked "*" are required.
            </p>
          </div>
          <div className="text-right text-sm">
            <p>KCB Towers, Kenya Road</p>
            <p>P.O. Box 21046-00100</p>
            <p>Tel: +254111485951</p>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Tender Details */}
          <fieldset className="border border-border rounded-lg p-4">
            <legend className="px-2 text-sm font-semibold text-primary">Tender Details</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <Label htmlFor="tenderNo">Tender Number *</Label>
                <Input id="tenderNo" {...register('tenderNo')} placeholder="e.g., NATCON/ICT/03/2025" />
                {errors.tenderNo && <p className="text-destructive text-sm">{errors.tenderNo.message}</p>}
              </div>
              <div>
                <Label htmlFor="tenderTitle">Tender Title *</Label>
                <Input id="tenderTitle" {...register('tenderTitle')} placeholder="e.g., Supply and Delivery of Laptops" />
                {errors.tenderTitle && <p className="text-destructive text-sm">{errors.tenderTitle.message}</p>}
              </div>
              <div>
                <Label htmlFor="category">Category *</Label>
                <Select onValueChange={(value) => setValue('category', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="goods">Goods</SelectItem>
                    <SelectItem value="works">Works</SelectItem>
                    <SelectItem value="services">Services</SelectItem>
                    <SelectItem value="consultancy">Consultancy</SelectItem>
                  </SelectContent>
                </Select>
                {errors.category && <p className="text-destructive text-sm">{errors.category.message}</p>}
              </div>
              <div>
                <Label htmlFor="lot">Lot (if applicable)</Label>
                <Input id="lot" {...register('lot')} placeholder="Lot number/name if applicable" />
              </div>
            </div>
          </fieldset>

          {/* Bidder Information */}
          <fieldset className="border border-border rounded-lg p-4">
            <legend className="px-2 text-sm font-semibold text-primary">Bidder Information</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <Label htmlFor="legalName">Legal Entity Name *</Label>
                <Input id="legalName" {...register('legalName')} />
                {errors.legalName && <p className="text-destructive text-sm">{errors.legalName.message}</p>}
              </div>
              <div>
                <Label htmlFor="tradingName">Trading Name (if any)</Label>
                <Input id="tradingName" {...register('tradingName')} />
              </div>
              <div>
                <Label htmlFor="entityType">Entity Type *</Label>
                <Select onValueChange={(value) => setValue('entityType', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select entity type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="company">Company</SelectItem>
                    <SelectItem value="partnership">Partnership</SelectItem>
                    <SelectItem value="sole-proprietor">Sole Proprietor</SelectItem>
                    <SelectItem value="cooperative">Cooperative/SACCO</SelectItem>
                    <SelectItem value="ngo">NGO/Nonprofit</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {errors.entityType && <p className="text-destructive text-sm">{errors.entityType.message}</p>}
              </div>
              <div>
                <Label htmlFor="regNo">Registration/Inc. No. *</Label>
                <Input id="regNo" {...register('regNo')} />
                {errors.regNo && <p className="text-destructive text-sm">{errors.regNo.message}</p>}
              </div>
              <div>
                <Label htmlFor="kraPin">KRA PIN *</Label>
                <Input id="kraPin" {...register('kraPin')} placeholder="Format: A123456789B" />
                {errors.kraPin && <p className="text-destructive text-sm">{errors.kraPin.message}</p>}
              </div>
              <div>
                <Label htmlFor="vatStatus">VAT Status</Label>
                <Select onValueChange={(value) => setValue('vatStatus', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select VAT status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="registered">VAT Registered</SelectItem>
                    <SelectItem value="exempt">VAT Exempt/Not Registered</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="contactName">Primary Contact Person *</Label>
                <Input id="contactName" {...register('contactName')} />
                {errors.contactName && <p className="text-destructive text-sm">{errors.contactName.message}</p>}
              </div>
              <div>
                <Label htmlFor="contactEmail">Contact Email *</Label>
                <Input id="contactEmail" type="email" {...register('contactEmail')} />
                {errors.contactEmail && <p className="text-destructive text-sm">{errors.contactEmail.message}</p>}
              </div>
              <div>
                <Label htmlFor="contactPhone">Contact Phone *</Label>
                <Input id="contactPhone" {...register('contactPhone')} placeholder="+2547XXXXXXXX" />
                {errors.contactPhone && <p className="text-destructive text-sm">{errors.contactPhone.message}</p>}
              </div>
              <div>
                <Label htmlFor="address">Physical/Postal Address</Label>
                <Input id="address" {...register('address')} placeholder="Building, Street, Town; P.O. Box" />
              </div>
            </div>
          </fieldset>

          {/* Eligibility and Preference */}
          <fieldset className="border border-border rounded-lg p-4">
            <legend className="px-2 text-sm font-semibold text-primary">Eligibility and Preference</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <Label htmlFor="agpoCategory">AGPO Category (if applicable)</Label>
                <Select onValueChange={(value) => setValue('agpoCategory', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Not applicable" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="women">Women</SelectItem>
                    <SelectItem value="youth">Youth</SelectItem>
                    <SelectItem value="pwd">Persons with Disabilities</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="agpoCertNo">AGPO Certificate No.</Label>
                <Input id="agpoCertNo" {...register('agpoCertNo')} placeholder="If applicable" />
              </div>
            </div>
          </fieldset>

          {/* Beneficial Ownership */}
          <fieldset className="border border-border rounded-lg p-4">
            <legend className="px-2 text-sm font-semibold text-primary">Beneficial Ownership (BO)</legend>
            <div className="mt-4">
              <Label htmlFor="boDisclosure">Beneficial Owners Summary *</Label>
              <Textarea 
                id="boDisclosure" 
                {...register('boDisclosure')} 
                placeholder="List full names, ID/passport, nationality, % ownership/control for each BO"
                className="min-h-24"
              />
              {errors.boDisclosure && <p className="text-destructive text-sm">{errors.boDisclosure.message}</p>}
            </div>
          </fieldset>

          {/* Declarations */}
          <fieldset className="border border-border rounded-lg p-4">
            <legend className="px-2 text-sm font-semibold text-primary">Declarations</legend>
            <div className="space-y-4 mt-4">
              <div className="flex items-start space-x-2">
                <Checkbox 
                  id="ethics" 
                  onCheckedChange={(checked) => setValue('ethics', checked === true)}
                />
                <Label htmlFor="ethics" className="text-sm leading-5">
                  We commit to the Code of Ethics for persons participating in public procurement and asset disposal, 
                  and confirm no debarment by PPRA.
                </Label>
              </div>
              {errors.ethics && <p className="text-destructive text-sm">{errors.ethics.message}</p>}
              
              <div className="flex items-start space-x-2">
                <Checkbox 
                  id="conflict" 
                  onCheckedChange={(checked) => setValue('conflict', checked === true)}
                />
                <Label htmlFor="conflict" className="text-sm leading-5">
                  We declare no conflict of interest with Natcon SACCO in relation to this tender and that 
                  information provided is true and complete.
                </Label>
              </div>
              {errors.conflict && <p className="text-destructive text-sm">{errors.conflict.message}</p>}
              
              <div className="flex items-start space-x-2">
                <Checkbox 
                  id="dpConsent" 
                  onCheckedChange={(checked) => setValue('dpConsent', checked === true)}
                />
                <Label htmlFor="dpConsent" className="text-sm leading-5">
                  We consent to the collection and processing of our contact and corporate personal data for 
                  tender administration in accordance with the Data Protection Act, 2019.
                </Label>
              </div>
              {errors.dpConsent && <p className="text-destructive text-sm">{errors.dpConsent.message}</p>}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="signName">Authorized Signatory (Full Name) *</Label>
                  <Input id="signName" {...register('signName')} />
                  {errors.signName && <p className="text-destructive text-sm">{errors.signName.message}</p>}
                </div>
                <div>
                  <Label htmlFor="signTitle">Designation/Title *</Label>
                  <Input id="signTitle" {...register('signTitle')} />
                  {errors.signTitle && <p className="text-destructive text-sm">{errors.signTitle.message}</p>}
                </div>
                <div>
                  <Label htmlFor="signDate">Date *</Label>
                  <Input id="signDate" type="date" {...register('signDate')} />
                  {errors.signDate && <p className="text-destructive text-sm">{errors.signDate.message}</p>}
                </div>
                <div>
                  <Label htmlFor="bidSecurity">Bid Security Details (if required)</Label>
                  <Input id="bidSecurity" {...register('bidSecurity')} placeholder="Issuer/Ref/Amount/Expiry" />
                </div>
              </div>
            </div>
          </fieldset>

          <div className="flex gap-4 pt-4">
            <Button type="submit" disabled={isSubmitting} className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              {isSubmitting ? 'Processing...' : 'Submit Application'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
          
          <p className="text-sm text-muted-foreground">
            By submitting, you acknowledge Natcon SACCO will verify KRA TCC and other eligibility information with relevant authorities.
          </p>
        </form>
      </CardContent>
    </Card>
  );
};

export default TenderRegistrationForm;